// M12 - MotricityCodeLinker
//
// Panel para vincular el codigo alfanumerico generado por un juego VR
// externo (A-Frame/Unity) a la sesion activa del terapeuta + UN
// paciente concreto.
//
// Solo se monta cuando la escena NO es de Mozilla Hubs (los hubs ya
// tienen su propio flujo de embed). Tras vincular, polling cada 15s
// para refrescar el estado de cada codigo (pending -> completed).

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Radio,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  AppstoreOutlined,
  LinkOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ReviMotricityApi } from "../../../../api/revi/reviMotricityApi";
import { detectGameFromScene } from "./detectGameFromScene";
import "./MotricityCodeLinker.scss";

const { Text, Paragraph } = Typography;

// Regex local para validar el code antes de pegar la llamada al server.
// Coincide con CODE_REGEX del server (Crockford Base32, 4-8 chars).
const CODE_REGEX = /^[A-HJ-NP-Z2-9]{4,8}$/;
const POLL_MS = 15000;

const api = new ReviMotricityApi();

function StatusTag({ status }) {
  const { t } = useTranslation("motricity");

  if (status === "completed") {
    return (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        {t("linker.status.completed")}
      </Tag>
    );
  }
  if (status === "partial") {
    return (
      <Tag color="orange" icon={<CheckCircleOutlined />}>
        {t("linker.status.partial")}
      </Tag>
    );
  }
  if (status === "expired") {
    return (
      <Tag color="default" icon={<CloseCircleOutlined />}>
        {t("linker.status.expired")}
      </Tag>
    );
  }
  if (status === "failed") {
    return (
      <Tag color="red" icon={<CloseCircleOutlined />}>
        {t("linker.status.failed")}
      </Tag>
    );
  }
  return (
    <Tag color="gold" icon={<ClockCircleOutlined spin />}>
      {t("linker.status.pending")}
    </Tag>
  );
}

export function MotricityCodeLinker({
  accessToken,
  activeSessionId,
  patients,
  sceneIndex,
  onSessionCompleted,
  // M12 live stream: el padre (PageTherapistSesionInner) necesita saber
  // cual es el code activo (pending) y su gameKey/supportsLiveStream para
  // decidir si abre el LiveStreamViewer en la tab "Sala". Lo notificamos
  // via callback cada vez que cambia. Cero acoplamiento extra: si el
  // padre no lo necesita, ignora el callback.
  onActiveLinkChange,
  // M12.2 - auto-deteccion del juego desde la escena actual. Si el
  // padre nos pasa la URL y el titulo de la escena, intentamos matchear
  // contra el catalogo de games y autoseleccionar el gameKey. Asi el
  // terapeuta no tiene que tocar el dropdown ni arriesgarse a meter el
  // codigo en el juego equivocado.
  sceneUrl,
  sceneTitle,
  // M12.2 - notifica al padre el resultado de la deteccion. El padre
  // lo usa para decidir si la escena es un juego VR identificado y
  // sustituye el iframe legacy por LiveStreamViewer/placeholder.
  onSceneDetection,
}) {
  // Dos namespaces: el propio del area y el comun, del que sale "Paciente".
  const { t } = useTranslation(["motricity", "common"]);
  const [code, setCode] = useState("");
  // Catalogo de juegos cargado dinamicamente desde el server (M12.1).
  // La fuente de verdad es la coleccion `revimotricitygames` gestionada
  // desde /admin/revi?tab=motricity-games. Si hay >1 juego enabled,
  // mostramos un Select; si hay solo 1, queda implicito y no molesta.
  const [games, setGames] = useState([]);
  const [gameKey, setGameKey] = useState(null);
  const [gamesLoading, setGamesLoading] = useState(false);
  // M12.2 - juego auto-detectado por heuristica URL/titulo. Si tiene
  // valor, ocultamos el dropdown y mostramos un Tag de solo lectura.
  const [detectedGame, setDetectedGame] = useState(null);
  const [patientId, setPatientId] = useState(
    patients?.length === 1 ? patients[0]._id : null,
  );
  const [linking, setLinking] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const lastSeenCompleted = useRef(new Set());

  // Carga del catalogo de juegos. Solo al montar; el linker es efimero
  // (solo vive durante una sesion) asi que no necesita refresh.
  useEffect(() => {
    if (!accessToken) return undefined;
    let alive = true;
    (async () => {
      try {
        setGamesLoading(true);
        const res = await api.listEnabledGames(accessToken);
        const list = res?.items || [];
        if (!alive) return;
        setGames(list);
        if (list.length > 0 && !gameKey) {
          setGameKey(list[0].gameKey);
        }
      } catch (e) {
        if (alive) {
          console.error("[motricity-linker] listEnabledGames error", e);
        }
      } finally {
        if (alive) setGamesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // gameKey omitido a proposito: la carga es one-shot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // M12.2 - auto-deteccion del juego cuando cambia el catalogo o la
  // escena. Se ejecuta tantas veces como sea necesario; el efecto solo
  // emite cambios cuando hay alguna diferencia respecto al detectedGame
  // actual.
  useEffect(() => {
    const hit = detectGameFromScene(games, sceneUrl, sceneTitle);
    setDetectedGame(hit || null);
    if (hit) setGameKey(hit.gameKey);
    if (onSceneDetection) {
      onSceneDetection(
        hit
          ? {
              detected: true,
              gameKey: hit.gameKey,
              label: hit.label,
              supportsLiveStream: hit.supportsLiveStream === true,
            }
          : { detected: false },
      );
    }
    // onSceneDetection es referencial del padre; no lo metemos en deps
    // para evitar re-emisiones innecesarias.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games, sceneUrl, sceneTitle]);

  // Carga inicial + polling.
  const refresh = useCallback(async () => {
    if (!accessToken || !activeSessionId) return;
    try {
      setLoadingItems(true);
      const res = await api.listByActiveSession(accessToken, activeSessionId);
      const next = res?.items || [];
      setItems(next);

      // Notificar cuando un code nuevo pase a completed.
      next.forEach((s) => {
        if (
          (s.status === "completed" || s.status === "partial") &&
          !lastSeenCompleted.current.has(s.id)
        ) {
          lastSeenCompleted.current.add(s.id);
          onSessionCompleted?.(s);
        }
      });
    } catch (e) {
      console.error("[motricity] listByActive error", e);
    } finally {
      setLoadingItems(false);
    }
  }, [accessToken, activeSessionId, onSessionCompleted]);

  // M12 live stream: el "link activo" es el ultimo item en pending.
  // En la practica el terapeuta solo trabaja con un code a la vez por
  // sesion VR, asi que la heuristica es "el mas reciente pending". Si
  // hay 0 pending, notificamos null.
  useEffect(() => {
    if (!onActiveLinkChange) return;
    const pendingItems = (items || []).filter((s) => s.status === "pending");
    if (pendingItems.length === 0) {
      onActiveLinkChange(null);
      return;
    }
    // Mas reciente = mayor createdAt. Si no hay createdAt, usamos el ultimo.
    const sorted = [...pendingItems].sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta;
    });
    const active = sorted[0];
    const gameMeta = (games || []).find((g) => g.gameKey === active.gameKey);
    onActiveLinkChange({
      id: active.id,
      code: active.code,
      gameKey: active.gameKey,
      gameLabel: gameMeta?.label || null,
      supportsLiveStream: gameMeta?.supportsLiveStream === true,
      patientId: active.patient,
    });
  }, [items, games, onActiveLinkChange]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, POLL_MS);
    return () => clearInterval(t);
  }, [refresh]);

  // Mantener patientId si solo hay 1 paciente.
  useEffect(() => {
    if (patients?.length === 1) setPatientId(patients[0]._id);
  }, [patients]);

  const canLink = useMemo(() => {
    if (!patientId) return false;
    if (!gameKey) return false;
    return CODE_REGEX.test(String(code).toUpperCase());
  }, [code, gameKey, patientId]);

  const handleLink = async () => {
    setLinkError(null);
    setLinking(true);
    try {
      const upper = String(code).toUpperCase().trim();
      await api.linkCode(accessToken, {
        code: upper,
        gameKey,
        activeSessionId,
        patientId,
        sceneIndex,
      });
      message.success(t("codeLinker.vinculado", { codigo: upper }));
      setCode("");
      refresh();
    } catch (e) {
      const msg =
        e?.body?.message || e?.message || t("linker.errors.linkFailedFallback");
      setLinkError(msg);
    } finally {
      setLinking(false);
    }
  };

  const showPatientPicker = (patients || []).length > 1;

  return (
    <Card
      className="motricity-linker-card"
      title={
        <Space>
          <AppstoreOutlined />
          <span>{t("linker.title")}</span>
        </Space>
      }
      extra={
        <Tooltip title={t("linker.refresh")}>
          <Button
            size="small"
            type="text"
            icon={<ReloadOutlined spin={loadingItems} />}
            onClick={refresh}
          />
        </Tooltip>
      }
    >
      <Paragraph type="secondary" style={{ marginTop: 0 }}>
        {t("linker.intro")}
      </Paragraph>

      {linkError ? (
        <Alert
          type="error"
          showIcon
          message={t("linker.errors.linkFailedTitle")}
          description={linkError}
          closable
          onClose={() => setLinkError(null)}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      {detectedGame ? (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            {t("linker.sceneGame")}
          </Text>
          <Tag color="cyan" style={{ fontSize: 13, padding: "4px 10px" }}>
            {detectedGame.label}
          </Tag>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
            {t("linker.autoDetected")}
          </Text>
        </div>
      ) : games.length > 1 ? (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            {t("linker.gamePickerLabel")}
          </Text>
          <Select
            style={{ width: "100%" }}
            value={gameKey}
            onChange={setGameKey}
            loading={gamesLoading}
            options={games.map((g) => ({ value: g.gameKey, label: g.label }))}
          />
        </div>
      ) : null}

      {showPatientPicker ? (
        <div style={{ marginBottom: 12 }}>
          <Text strong style={{ display: "block", marginBottom: 6 }}>
            {t("linker.patientPickerLabel")}
          </Text>
          <Radio.Group
            value={patientId}
            onChange={(e) => setPatientId(e.target.value)}
          >
            <Space wrap>
              {patients.map((p) => (
                <Radio key={p._id} value={p._id}>
                  {p.name} {p.surname || ""}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      ) : null}

      {!gamesLoading && games.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message={t("linker.noGames.title")}
          description={t("linker.noGames.description")}
          style={{ marginBottom: 12 }}
        />
      ) : null}

      <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
        <Input
          placeholder={t("linker.codePlaceholder")}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          maxLength={8}
          onPressEnter={() => canLink && !linking && handleLink()}
          style={{
            textTransform: "uppercase",
            letterSpacing: 2,
            fontWeight: 600,
          }}
        />
        <Button
          type="primary"
          icon={<LinkOutlined />}
          loading={linking}
          disabled={!canLink}
          onClick={handleLink}
        >
          {t("linker.linkAction")}
        </Button>
      </Space.Compact>

      <div className="motricity-linker-list">
        {items.length === 0 ? (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t("linker.emptyList")}
          </Text>
        ) : (
          items.map((s) => {
            const patient = (patients || []).find(
              (p) => String(p._id) === String(s.patient),
            );
            // El nombre del paciente viene de la BD y no se traduce; solo
            // el texto de reserva cuando no se encuentra, que si es interfaz.
            const patientLabel = patient
              ? `${patient.name} ${patient.surname || ""}`.trim()
              : t("common:roles.patient");
            return (
              <div key={s.id} className="motricity-linker-list__row">
                <div className="motricity-linker-list__code">{s.code}</div>
                <div className="motricity-linker-list__meta">
                  <StatusTag status={s.status} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {patientLabel}
                  </Text>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
